import React, { useState, useEffect } from 'react';
import {
    Search,
    Download,
    MessageSquare,
    User,
    Clock,
    CheckCircle,
    Archive,
    Trash2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import ApiService from '../models/ApiService';

function UserChats() {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [expandedUser, setExpandedUser] = useState(null);
    const [selectedChats, setSelectedChats] = useState([]);
    const [searchResults, setSearchResults] = useState({
        userMatches: 0,
        chatMatches: 0,
        expandedChats: []
    });

    // Fetch user chats on component mount
    useEffect(() => {
        fetchUserChats();
    }, []);

    const fetchUserChats = async () => {
        try {
            setLoading(true);
            const res = await ApiService.GetAllUserChats();
            console.log(res);

            if (res.data.responseContent) {
                // Transform API data to match our component structure
                const transformedUsers = res.data.responseContent.map(userData => ({
                    id: userData._id._id,
                    userId: userData._id._id,
                    userName: `${userData._id.first_name || ''} ${userData._id.last_name || ''}`.trim() || 'Unknown User',
                    userEmail: userData._id.email || userData._id.mobile || 'No contact',
                    mobile: userData._id.mobile,
                    totalChats: userData.chats?.length || 0,
                    lastChat: userData.chats?.length > 0 ? userData.chats[userData.chats.length - 1] : null,
                    chats: userData.chats || [],
                    __v: userData.__v
                }));

                setUsers(transformedUsers);
                setFilteredUsers(transformedUsers);
            }
        } catch (error) {
            console.error('Error fetching user chats:', error);
        } finally {
            setLoading(false);
        }
    };

    // Enhanced search functionality
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredUsers(users);
            setSearchResults({
                userMatches: users.length,
                chatMatches: 0,
                expandedChats: []
            });
            return;
        }

        const searchLower = searchTerm.toLowerCase();
        let totalChatMatches = 0;
        const usersWithMatches = [];
        const expandedChatIds = [];

        users.forEach(user => {
            let userHasMatch = false;
            let userChatMatches = 0;

            // Check if user name matches
            if (user.userName.toLowerCase().includes(searchLower)) {
                userHasMatch = true;
            }

            // Check if any chat matches (question or answer)
            const filteredChats = user.chats.filter(chat => {
                const questionMatch = chat.question.toLowerCase().includes(searchLower);
                const answerMatch = chat.answer.toLowerCase().includes(searchLower);
                return questionMatch || answerMatch;
            });

            if (filteredChats.length > 0) {
                userHasMatch = true;
                userChatMatches = filteredChats.length;
                totalChatMatches += userChatMatches;

                // Auto-expand users with matching chats
                expandedChatIds.push(user.id);
            }

            if (userHasMatch) {
                // Highlight matching text in chats
                const userWithHighlightedChats = {
                    ...user,
                    chats: user.chats.map(chat => ({
                        ...chat,
                        highlightedQuestion: highlightText(chat.question, searchTerm),
                        highlightedAnswer: highlightText(chat.answer, searchTerm),
                        hasMatch: chat.question.toLowerCase().includes(searchLower) ||
                            chat.answer.toLowerCase().includes(searchLower)
                    }))
                };
                usersWithMatches.push(userWithHighlightedChats);
            }
        });

        setFilteredUsers(usersWithMatches);
        setSearchResults({
            userMatches: usersWithMatches.length,
            chatMatches: totalChatMatches,
            expandedChats: expandedChatIds
        });

        // Auto-expand users with matching chats if search is active
        if (searchTerm && expandedChatIds.length > 0) {
            setExpandedUser(expandedChatIds[0]);
        } else if (!searchTerm) {
            setExpandedUser(null);
        }
    }, [searchTerm, users]);

    // Function to highlight matching text
    const highlightText = (text, searchTerm) => {
        if (!searchTerm.trim()) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
    };

    // Toggle user selection
    const toggleUserSelection = (userId) => {
        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
        } else {
            setSelectedUsers([...selectedUsers, userId]);
        }
    };

    // Toggle chat selection
    const toggleChatSelection = (chatId, userId) => {
        const chatKey = `${userId}-${chatId}`;
        if (selectedChats.includes(chatKey)) {
            setSelectedChats(selectedChats.filter(key => key !== chatKey));
        } else {
            setSelectedChats([...selectedChats, chatKey]);
        }
    };

    // Toggle user expansion
    const toggleUserExpansion = (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
        } else {
            setExpandedUser(userId);
        }
    };

    // Delete selected chats
    const deleteSelectedChats = async () => {
        try {
            const updatedUsers = users.map(user => {
                if (selectedChats.some(chatKey => chatKey.startsWith(user.id))) {
                    const updatedChats = user.chats.filter(chat =>
                        !selectedChats.includes(`${user.id}-${chat._id}`)
                    );
                    return { ...user, chats: updatedChats, totalChats: updatedChats.length };
                }
                return user;
            });

            setUsers(updatedUsers);
            setFilteredUsers(updatedUsers);
            setSelectedChats([]);
        } catch (error) {
            console.error('Error deleting chats:', error);
        }
    };

    // Calculate stats
    const calculateStats = () => {
        const totalUsers = users.length;
        const totalChats = users.reduce((sum, user) => sum + user.totalChats, 0);
        const totalMessages = users.reduce((sum, user) => sum + (user.chats?.reduce((chatSum, chat) => chatSum + 2, 0) || 0), 0);
        const activeToday = users.filter(user => {
            if (!user.lastChat) return false;
            const lastChatDate = new Date(`${user.lastChat.date} ${user.lastChat.time}`);
            const today = new Date();
            return lastChatDate.toDateString() === today.toDateString();
        }).length;

        return { totalUsers, totalChats, totalMessages, activeToday };
    };

    const stats = calculateStats();

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#144740] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading user chats...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-[--text-color-g]" >
                                User Chats
                            </h1>
                            <p className="text-black mt-1">Monitor and manage all user conversations</p>
                        </div>
                    </div>
                </div>

                {/* Search Bar with Results Summary */}
                <div className=" p-4 rounded-lg mb-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search users by name, questions, or answers..."
                            className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] focus:outline-none focus:ring-1 focus:ring-[--border-color-o] rounded-lg"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Search Results Summary */}
                    {searchTerm && (
                        <div className="mt-3 flex items-center space-x-4 text-sm text-gray-600">
                            <span className="px-3 py-1 bg-blue-50 rounded-full">
                                Found {searchResults.userMatches} users
                            </span>
                            <span className="px-3 py-1 bg-green-50 rounded-full">
                                {searchResults.chatMatches} matching chats
                            </span>
                            <button
                                onClick={() => setSearchTerm('')}
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                Clear search
                            </button>
                        </div>
                    )}
                </div>

                {/* User List */}
                <div className="space-y-4">
                    {filteredUsers.length === 0 ? (
                        <div className="bg-white rounded-lg shadow p-12 text-center">
                            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                            <p className="text-gray-500 mb-4">
                                {searchTerm ? 'Try adjusting your search term' : 'No user chat history available'}
                            </p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="text-blue-600 hover:text-blue-900 font-medium"
                                    style={{ color: 'var(--primary-color, #144740)' }}
                                >
                                    Clear search
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredUsers.map(user => (
                            <div key={user.id} className="bg-white rounded-lg border border-[--border-color-g] shadow overflow-hidden">
                                {/* User Header */}
                                <div
                                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => toggleUserExpansion(user.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div
                                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                                                style={{ backgroundColor: 'var(--primary-color, #144740)' }}
                                            >
                                                {user.userName.charAt(0)}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900">
                                                    {searchTerm && user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ? (
                                                        <span dangerouslySetInnerHTML={{
                                                            __html: highlightText(user.userName, searchTerm)
                                                        }} />
                                                    ) : (
                                                        user.userName
                                                    )}
                                                </h3>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-6">
                                            <div className="text-right">
                                                <span className="text-sm font-semibold text-[--text-color-g]">Total Chats</span>
                                                <p className="font-semibold text-[--text-color]">{user.totalChats}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm font-semibold text-[--text-color-g]">Last Active</span>
                                                <p className="font-semibold text-[--text-color]">
                                                    {user.lastChat ? (`${user.lastChat.date} ${user.lastChat.time}`) : 'Never'}
                                                </p>
                                            </div>
                                            <div>
                                                {expandedUser === user.id ? (
                                                    <ChevronUp className="w-5 h-5 text-gray-500" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Chat History */}
                                {expandedUser === user.id && user.chats.length > 0 && (
                                    <div className="border-t border-gray-200">
                                        <div className="p-4 bg-gray-50">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="font-semibold text-[--text-color-g]">Chat History</h4>
                                                <span className="text-sm text-gray-500">
                                                    {user.chats.length} conversations
                                                    {searchTerm && (
                                                        <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                                                            {user.chats.filter(chat => chat.hasMatch).length} matches
                                                        </span>
                                                    )}
                                                </span>
                                            </div>

                                            <div className="space-y-4">
                                                {user.chats.map(chat => (
                                                    <div
                                                        key={chat._id}
                                                        className={`bg-white rounded-lg p-4 hover:shadow-md transition-shadow hover:border-[--border-color-g] border cursor-pointer ${chat.hasMatch ? 'bg-yellow-50 border-yellow-200' : ''
                                                            }`}
                                                    >
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div>
                                                                <div className="flex items-center space-x-2 mb-2">
                                                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                                                        #{chat.seq_no}
                                                                    </span>
                                                                    <span className="text-sm text-gray-500">
                                                                        {chat.date} • {chat.time}
                                                                    </span>
                                                                    {chat.hasMatch && (
                                                                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                                                            Match
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="font-medium text-[--text-color-g] mb-2">
                                                                    <span className="font-semibold">Q:</span>
                                                                    <span
                                                                        className="ml-2"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: chat.highlightedQuestion || chat.question
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="pl-4 border-l-4" style={{ borderLeftColor: 'var(--secondary-color, #F59324)' }}>
                                                            <div className="text-[--text-color-g] whitespace-pre-line">
                                                                <span className="font-semibold">A:</span>
                                                                <span
                                                                    className="ml-2"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: chat.highlightedAnswer || chat.answer
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>

                                                        {chat.is_deleted && (
                                                            <div className="mt-2 inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                                                Deleted
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* No chats message */}
                                {expandedUser === user.id && user.chats.length === 0 && (
                                    <div className="border-t border-gray-200 p-8 text-center">
                                        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500">No chat history for this user</p>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Footer Stats */}
                <div className="mt-8 text-center text-gray-500 text-sm">
                    Showing {filteredUsers.length} of {users.length} users
                    {searchTerm && (
                        <span className="ml-2">
                            • {searchResults.chatMatches} chat matches found
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserChats;